// The four frameworks the Revision & rapporter page can cite. Every requirement row in the report
// (one per check NordComply actually makes) maps to exactly ONE representative clause per framework
// — the same "one headline control per check" approach the old per-section mapping used, not every
// possible sub-requirement. `ref[row] = [clause, the framework's own name for it]`.
//
// ISO 27001:2022 Annex A, NIS2 Art. 21(2)(a-j) and GDPR articles are cited as they appear in the
// texts; which clause best fits which technical check is a judgement call, so an auditor/lawyer
// should sign the mapping off before it's presented as authoritative.
//
// CIS Controls v8 ("CIS 18", the `cis18` key also used on Compliance) numbers per topic. The CIS Microsoft 365
// Benchmark section numbers that used to sit next to them were guesses and have been removed: nothing is cited here
// that has not been checked against a source. (The same goes for Microsoft's own Secure Score, which is shown on
// Compliance under its own name and is not a framework in the reports.)
import { CIS_REF_BY_TOPIC } from './cisRefByTopic.generated';

const CIS_COMBINED_REF = {
  mfa: ['v8 6.3', 'Kræv MFA'],
  guests: ['v8 5.3', 'Deaktivér inaktive konti'],
  admin: ['v8 5.4', 'Begræns administratorrettigheder'],
  device: ['v8 3.6', 'Kryptér slutbrugerenheder'],
  inc: ['v8 17.3', 'Rapportering af hændelser'],
  apps: ['v8 15.1', 'Gennemgå tredjepartsadgang'],
  mail: ['v8 Control 9', 'Kontrollér mailregler'],
  share: ['v8 3.3', 'Kontrollér ekstern deling'],
  'mfa-all': ['v8 6.3', 'Kræv MFA for alle'],
  'mfa-admin': ['v8 6.5', 'Kræv MFA for administratorer'],
  'mfa-guest': ['v8 6.4', 'Kræv MFA for gæster'],
  'mfa-azure': ['v8 6.3', 'Kræv MFA til Azure-administration'],
  'guest-admin-block': ['v8 6.8', 'Begræns gæsters adgang til adminportaler'],
  'legacy-auth': ['v8 4.8', 'Bloker ældre godkendelsesprotokoller'],
  'device-access-control': ['v8 13.5', 'Kræv enhedsoverholdelse ved login'],
  'session-control': ['v8 4.3', 'Begræns sessionsvarighed'],
  'location-access': ['v8 12.2', 'Betinget adgang baseret på lokation'],
  'mobile-app-protection': ['v8 4.12', 'Kræv app-beskyttelse på mobile enheder'],
  'device-encryption': ['v8 3.6', 'Kryptér slutbrugerenheder'],
  'device-firewall': ['v8 4.1', 'Aktivér firewall på enheder'],
  'device-antimalware': ['v8 10.1', 'Aktivér virusbeskyttelse'],
  'device-edr': ['v8 13.2', 'Tilslut Defender for Endpoint'],
  'device-auth': ['v8 5.2', 'Kræv adgangskode eller PIN på enheder'],
  'device-admin-password': ['v8 5.4', 'Unik lokal administratoradgangskode'],
  'device-patching': ['v8 7.3', 'Automatiske enhedsopdateringer'],
  'device-backup': ['v8 11.2', 'Automatisk sikkerhedskopiering'],
  'device-app-control': ['v8 2.5', 'Kun godkendt software på enheder'],
  'device-enrollment': ['v8 4.1', 'Styret tilmelding af enheder'],
  'mail-spam': ['v8 9.2', 'Filtrering af spam og phishing'],
  'mail-phishing': ['v8 9.7', 'Beskyttelse mod links og vedhæftninger'],
  'mail-domain-auth': ['v8 9.5', 'SPF, DKIM og DMARC opsat korrekt'],
};
// The CIS number(s) cited per topic. For every topic that is made of checks they come from the checks themselves
// (cisRefByTopic.generated.js, written from controls.js), so the reports and Compliance can never cite different numbers.
// The coarse report rows (mfa, guests, admin, ...) keep the number written above.
function cisRef() {
  return Object.fromEntries(Object.entries(CIS_COMBINED_REF).map(([key, [combined, label]]) => [key, [CIS_REF_BY_TOPIC[key] || combined.replace(/^v8 /, ''), label]]));
}
function cisFramework() {
  return {
    key: 'cis18',
    what: 'En liste med 18 tekniske sikkerhedskontroller (CIS Controls), ikke bundet til ét system. En generel, anerkendt målestok for grundlæggende cybersikkerhed.',
    who: 'Frivillig. En bredt anerkendt målestok for teknisk sikkerhed, uafhængig af leverandør.',
    law: false,
    label: 'CIS 18',
    kind: 'Anbefaling',
    tag: 'CIS Controls v8',
    lead: 'CIS 18 er en anerkendt, leverandøruafhængig liste over tekniske grundregler.',
    notes: [
      'Kontrollerne dækker Implementation Group 1 til 3, hovedsageligt IG1 og IG2.',
      'CIS er en anbefaling og ikke et lovkrav. Rapporten viser, hvor tæt jeres opsætning er på den.',
    ],
    ref: cisRef(),
  };
}

export const REPORT_FRAMEWORKS = [
  {
    key: 'iso',
    what: 'International standard for, hvordan en virksomhed styrer sin informationssikkerhed. En tredjepart kan certificere jer efter den.',
    who: 'Frivillig. Ofte krævet af kunder og samarbejdspartnere.',
    law: false,
    label: 'ISO 27001',
    kind: 'Standard',
    tag: 'ISO/IEC 27001:2022, Annex A',
    lead: 'ISO 27001 handler om, at sikkerhed styres og kan dokumenteres.',
    notes: [
      'Rapporten kan bruges som bilag til anvendelighedserklæringen (SoA) og til den interne audit.',
      'Beslutninger og risici svarer til risikohåndteringen i klausul 6.1.3.',
    ],
    ref: {
      mfa: ['A.8.5', 'Sikker autentifikation'],
      guests: ['A.5.18', 'Adgangsrettigheder'],
      admin: ['A.8.2', 'Privilegerede adgangsrettigheder'],
      device: ['A.8.1 · A.8.24', 'Slutbrugerenheder og kryptering'],
      inc: ['A.8.16', 'Overvågningsaktiviteter'],
      apps: ['A.5.19', 'Leverandørforhold'],
      mail: ['A.5.14', 'Informationsoverførsel'],
      share: ['A.5.15', 'Adgangsstyring'],
      // Finere opdeling til Kontroller-siden (se controlFrameworks.js) — samme "én
      // paragraf pr. emne"-tilgang, bare med flere, mere specifikke emner end de 8 ovenfor.
      'mfa-all': ['A.8.5', 'Sikker autentifikation'],
      'mfa-admin': ['A.8.2', 'Privilegerede adgangsrettigheder'],
      'mfa-guest': ['A.5.18', 'Adgangsrettigheder for eksterne'],
      'mfa-azure': ['A.8.2', 'Privilegerede adgangsrettigheder'],
      'guest-admin-block': ['A.5.18 · A.8.3', 'Adgangsrettigheder og begrænsning af informationsadgang'],
      'legacy-auth': ['A.8.5', 'Sikker autentifikation'],
      'device-access-control': ['A.8.1 · A.8.20', 'Slutbrugerenheder og netværkssikkerhed'],
      'session-control': ['A.8.5', 'Sikker autentifikation'],
      'location-access': ['A.8.20', 'Netværkssikkerhed'],
      'mobile-app-protection': ['A.8.1', 'Slutbrugerenheder'],
      'device-encryption': ['A.8.24', 'Kryptografi'],
      'device-firewall': ['A.8.20', 'Netværkssikkerhed'],
      'device-antimalware': ['A.8.7', 'Beskyttelse mod malware'],
      'device-edr': ['A.8.16', 'Overvågningsaktiviteter'],
      'device-auth': ['A.8.5', 'Sikker autentifikation'],
      'device-admin-password': ['A.8.2', 'Privilegerede adgangsrettigheder'],
      'device-patching': ['A.8.8', 'Styring af tekniske sårbarheder'],
      'device-backup': ['A.8.13', 'Sikkerhedskopiering af information'],
      'device-app-control': ['A.8.19', 'Installation af software'],
      'device-enrollment': ['A.8.9', 'Konfigurationsstyring'],
      'mail-spam': ['A.5.14', 'Informationsoverførsel'],
      'mail-phishing': ['A.8.7', 'Beskyttelse mod malware'],
      'mail-domain-auth': ['A.5.14', 'Informationsoverførsel'],
    },
  },
  {
    key: 'nis2',
    what: 'EU-direktiv om cybersikkerhed. Ledelsen har ansvaret for, at virksomheden tager passende sikkerhedsforanstaltninger.',
    who: 'Lovkrav for de virksomheder, der er omfattet af direktivet.',
    law: true,
    label: 'NIS2',
    kind: 'EU-direktiv',
    tag: 'Direktiv (EU) 2022/2555, Art. 21(2)',
    lead: 'NIS2 stiller krav til ledelsens ansvar for cybersikkerhed.',
    notes: [
      'Rapporten dækker de tekniske foranstaltninger i Microsoft 365. Organisatoriske krav, fx beredskabsplaner og uddannelse, er ikke med.',
      'Alvorlige hændelser skal anmeldes efter Art. 23. Rapporten viser åbne alerts, men er ikke en anmeldelse.',
    ],
    ref: {
      mfa: ['Art. 21(2)(j)', 'Multifaktorgodkendelse'],
      guests: ['Art. 21(2)(i)', 'Adgangsstyring og aktiver'],
      admin: ['Art. 21(2)(i)', 'Adgangsstyring og aktiver'],
      device: ['Art. 21(2)(h)', 'Kryptering'],
      inc: ['Art. 21(2)(b)', 'Håndtering af hændelser'],
      apps: ['Art. 21(2)(d)', 'Forsyningskædesikkerhed'],
      mail: ['Art. 21(2)(g)', 'Grundlæggende cyberhygiejne'],
      share: ['Art. 21(2)(i)', 'Adgangsstyring og aktiver'],
      'mfa-all': ['Art. 21(2)(j)', 'Multifaktorgodkendelse'],
      'mfa-admin': ['Art. 21(2)(j)', 'Multifaktorgodkendelse for administratorer'],
      'mfa-guest': ['Art. 21(2)(j)', 'Multifaktorgodkendelse'],
      'mfa-azure': ['Art. 21(2)(j)', 'Multifaktorgodkendelse til administration'],
      'guest-admin-block': ['Art. 21(2)(i)', 'Adgangsstyring og aktiver'],
      'legacy-auth': ['Art. 21(2)(g)', 'Grundlæggende cyberhygiejne'],
      'device-access-control': ['Art. 21(2)(i)', 'Adgangsstyring og aktiver'],
      'session-control': ['Art. 21(2)(j)', 'Sikret sessionshåndtering'],
      'location-access': ['Art. 21(2)(i)', 'Adgangsstyring og aktiver'],
      'mobile-app-protection': ['Art. 21(2)(i)', 'Adgangsstyring og aktiver'],
      'device-encryption': ['Art. 21(2)(h)', 'Kryptering'],
      'device-firewall': ['Art. 21(2)(g)', 'Grundlæggende cyberhygiejne'],
      'device-antimalware': ['Art. 21(2)(g)', 'Grundlæggende cyberhygiejne'],
      'device-edr': ['Art. 21(2)(b)', 'Håndtering af hændelser'],
      'device-auth': ['Art. 21(2)(j)', 'Multifaktorgodkendelse'],
      'device-admin-password': ['Art. 21(2)(i)', 'Adgangsstyring og aktiver'],
      'device-patching': ['Art. 21(2)(e)', 'Sårbarhedshåndtering'],
      'device-backup': ['Art. 21(2)(c)', 'Driftskontinuitet og backup'],
      'device-app-control': ['Art. 21(2)(e)', 'Sikkerhed i systemudvikling og -drift'],
      'device-enrollment': ['Art. 21(2)(i)', 'Adgangsstyring og aktiver'],
      'mail-spam': ['Art. 21(2)(g)', 'Grundlæggende cyberhygiejne'],
      'mail-phishing': ['Art. 21(2)(g)', 'Grundlæggende cyberhygiejne'],
      'mail-domain-auth': ['Art. 21(2)(g)', 'Grundlæggende cyberhygiejne'],
    },
  },
  {
    key: 'gdpr',
    what: 'EU-forordning om beskyttelse af personoplysninger. Kræver passende teknisk og organisatorisk sikkerhed.',
    who: 'Lovkrav for alle, der behandler personoplysninger.',
    law: true,
    label: 'GDPR',
    kind: 'EU-forordning',
    tag: 'Forordning (EU) 2016/679',
    lead: 'GDPR kræver passende sikkerhed omkring personoplysninger.',
    notes: [
      'Beslutningsregistret dokumenterer ansvarlighed efter Art. 5(2): hvem der besluttede hvad, og hvornår det skal vurderes igen.',
      'Rapporten viser den tekniske sikkerhed i Microsoft 365. Den erstatter ikke databehandleraftaler eller en konsekvensanalyse.',
    ],
    ref: {
      mfa: ['Art. 32(1)(b)', 'Fortrolighed og robusthed'],
      guests: ['Art. 32(1)(d)', 'Regelmæssig test og vurdering'],
      admin: ['Art. 25', 'Databeskyttelse gennem design'],
      device: ['Art. 32(1)(a)', 'Kryptering af personoplysninger'],
      inc: ['Art. 33', 'Underretning om brud (72 timer)'],
      apps: ['Art. 28', 'Databehandlere'],
      mail: ['Art. 44–49', 'Overførsel ud af organisationens kontrol'],
      share: ['Art. 5(1)(f)', 'Integritet og fortrolighed'],
      'mfa-all': ['Art. 32(1)(b)', 'Fortrolighed og robusthed'],
      'mfa-admin': ['Art. 25', 'Databeskyttelse gennem design'],
      'mfa-guest': ['Art. 32(1)(d)', 'Regelmæssig test og vurdering'],
      'mfa-azure': ['Art. 25', 'Databeskyttelse gennem design'],
      'guest-admin-block': ['Art. 32(1)(d)', 'Regelmæssig test og vurdering'],
      'legacy-auth': ['Art. 32(1)(b)', 'Fortrolighed og robusthed'],
      'device-access-control': ['Art. 32(1)(b)', 'Fortrolighed og robusthed'],
      'session-control': ['Art. 32(1)(b)', 'Fortrolighed og robusthed'],
      'location-access': ['Art. 32(1)(d)', 'Regelmæssig test og vurdering'],
      'mobile-app-protection': ['Art. 32(1)(a)', 'Kryptering af personoplysninger'],
      'device-encryption': ['Art. 32(1)(a)', 'Kryptering af personoplysninger'],
      'device-firewall': ['Art. 32(1)(b)', 'Fortrolighed og robusthed'],
      'device-antimalware': ['Art. 32(1)(b)', 'Fortrolighed og robusthed'],
      'device-edr': ['Art. 33', 'Underretning om brud (72 timer)'],
      'device-auth': ['Art. 32(1)(b)', 'Fortrolighed og robusthed'],
      'device-admin-password': ['Art. 25', 'Databeskyttelse gennem design'],
      'device-patching': ['Art. 32(1)(d)', 'Regelmæssig test og vurdering'],
      'device-backup': ['Art. 32(1)(c)', 'Mulighed for at genoprette data'],
      'device-app-control': ['Art. 25', 'Databeskyttelse gennem design'],
      'device-enrollment': ['Art. 25', 'Databeskyttelse gennem design'],
      'mail-spam': ['Art. 32(1)(b)', 'Fortrolighed og robusthed'],
      'mail-phishing': ['Art. 32(1)(b)', 'Fortrolighed og robusthed'],
      'mail-domain-auth': ['Art. 32(1)(b)', 'Fortrolighed og robusthed'],
    },
  },
  cisFramework(),
];

export const FRAMEWORK_BY_KEY = Object.fromEntries(REPORT_FRAMEWORKS.map((f) => [f.key, f]));

// Drafts and schedules saved before this redesign stored the old selector
// labels — translate them so a saved draft still opens on something sensible.
// Old CIS option names (including the removed "CIS Microsoft 365" / "M365 CIS") open on CIS 18.
const LEGACY_FRAMEWORK_LABEL_TO_KEY = {
  Generel: 'iso',
  'CIS Microsoft 365': 'cis18',
  'CIS Controls v8': 'cis18',
  GDPR: 'gdpr',
  'ISO 27001': 'iso',
  NIS2: 'nis2',
  CIS: 'cis18',
  'M365 CIS': 'cis18',
  cis: 'cis18',
};
export function frameworkKeyFromSaved(value) {
  return LEGACY_FRAMEWORK_LABEL_TO_KEY[value] || 'iso';
}
